import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserSelectorModalService, MasterUser } from './user-selector-modal.service';
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  SelectionChangedEvent,
  PageChangedEvent
} from 'app/shared/components/smart-grid';

/** Datos de entrada para el modal */
export interface UserSelectorModalData {
  selectedUsers?: MasterUser[];
  title?: string;
}

/** Resultado devuelto al cerrar el modal */
export interface UserSelectorModalResult {
  users: MasterUser[];
}

@Component({
  selector: 'app-user-selector-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SmartGridComponent
  ],
  providers: [UserSelectorModalService],
  templateUrl: './user-selector-modal.component.html',
  styleUrl: './user-selector-modal.component.scss'
})
export class UserSelectorModalComponent {
  @ViewChild(SmartGridComponent) grid!: SmartGridComponent<MasterUser>;

  private readonly dialogRef = inject(MatDialogRef<UserSelectorModalComponent>);
  private readonly data = inject<UserSelectorModalData>(MAT_DIALOG_DATA, { optional: true });
  protected readonly service = inject(UserSelectorModalService);
  protected readonly searchControl = new FormControl('');

  readonly title: string;

  /** Toggle para mostrar/ocultar preview de seleccionados */
  showSelectedPreview = false;

  /** Definición de columnas para SmartGrid */
  readonly columnDefs: ColDef<MasterUser>[] = [
    {
      field: 'user_login_username',
      headerName: 'Usuario',
      width: 150,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'user_login_first_name',
      headerName: 'Nombre',
      width: 150,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'user_login_email',
      headerName: 'Email',
      width: 200,
      valueFormatter: (value) => value || '-'
    },
    {
      field: 'type',
      headerName: 'Tipo',
      width: 120,
      valueFormatter: (value) => value || '-'
    }
  ];

  /** Configuración del SmartGrid */
  readonly gridConfig: GridConfig<MasterUser> = {
    rowIdField: '_id',
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
      selectOnRowClick: false,
      rememberSelection: true
    },
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 10,
      pageSizeOptions: [10, 25, 50]
    },
    sorting: {
      enabled: false
    },
    search: {
      enabled: false
    },
    columnFilters: {
      enabled: false
    },
    styling: {
      maxHeight: '400px',
      stripedRows: true,
      density: 'comfortable'
    },
    emptyState: {
      message: 'No hay usuarios disponibles',
      showIcon: true
    }
  };

  constructor() {
    this.title = this.data?.title ?? 'Seleccionar Usuarios';

    if (this.data?.selectedUsers?.length) {
      this.service.initializeSelection(this.data.selectedUsers);
    }

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => this.service.setSearch$.next(value ?? ''));
  }

  onConfirm(): void {
    const result: UserSelectorModalResult = {
      users: this.service.selectedUsers()
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onPageChange(event: PageChangedEvent): void {
    this.service.setPage$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onSelectionChanged(event: SelectionChangedEvent<MasterUser>): void {
    this.service.setSelectedUsers(event.selectedRows);
  }

  onToggleUser(user: MasterUser): void {
    this.service.toggleUser$.next(user);
  }
}

export type { MasterUser } from './user-selector-modal.service';
